import React, { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Checkbox from '@mui/material/Checkbox';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import useCacheSPFile from 'Hooks/sharepoint/useCacheSPFile/useCacheSPFile';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import useClaims from 'Hooks/claims/useClaims/useClaims';
import { useStore } from 'Store/pages/forms/store';
import { EmailFormModal } from '../EmailFormModal/EmailFormModal';
import './forms-list.scss';
import { useUser } from 'context/UserContext';
import { GetProductNames } from '../../../api/product';
import { useQuery } from '@tanstack/react-query';
import { GetFormsList } from '../../../api/forms';
import { fetchSecuredFile, fetchSignedURL } from '../../../api/file';

const SwitchMemo = React.memo(Switch);
const TextFieldMemo = React.memo(TextField);
const SelectMemo = React.memo(Select);
const MenuItemMemo = React.memo(MenuItem);
const FiltersMemo = React.memo(Filters);
const states = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennesee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconson',
  WY: 'Wyoming',
};

const divisions = {
  CASG: 'CASG Only',
  ISD: 'ISD Only',
  'All Sales': 'All Sales',
};

function FormsList() {
  const user = useUser();
  const claims = useClaims([{ ClaimName: 'LOB', ClaimType: 'Value' }]);
  const [LOB, setLOB] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProductNames, setLoadingProductNames] = useState(true);
  const [filtering, setFiltering] = useState(true);
  const [formsList, setFormsList] = useState([]);
  const [filteredFormsList, setFilteredFormsList] = useState([]);
  const [filterActive, setFilterACtive] = useState(true);
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [products, setProducts] = useState([]);
  const [productNames, setProductNames] = useState();
  const [categories, setCategories] = useState([]);
  const [commonlyUsedFilter, setCommonlyUsedFilter] = useState(false);
  const [orderableFilter, setOrderableFilter] = useState([]);
  const [divisionsFilter, setDivisionsFilter] = useState([]);
  const [statesFilter, setStatesFilter] = useState([]);
  const [productsFilter, setProductsFilter] = useState([]);
  const [categoriesFilter, setCategoriesFilter] = useState([]);
  const [formToEmail, setFormToEmail] = useState();
  const searchRef = useRef();
  const filterIconClick = () => {
    filterActive ? setFilterACtive(false) : setFilterACtive(true);
  };

  function checkLOB(iD3Code) {
    if (LOB == undefined) return true;

    var isValid = LOB.find((o) => o == iD3Code);
    return isValid != null && isValid != undefined;
  }

  const filterTable = () => {
    setFiltering(true);
    let finalData = formsList;

    if (searchRef.current.value != '') {
      finalData = finalData.filter(
        (element) =>
          (element.Title &&
            element.Title.toLowerCase().includes(searchRef.current.value.toLowerCase())) ||
          (element.Name &&
            element.Name.toLowerCase().includes(searchRef.current.value.toLowerCase())) ||
          (element.FormNumber &&
            element.FormNumber.toLowerCase().includes(searchRef.current.value.toLowerCase())) ||
          (element.Keywords &&
            element.Keywords.toLowerCase().includes(searchRef.current.value.toLowerCase()))
      );
    }
    if (categoriesFilter.length > 0) {
      finalData = finalData.filter((element) => categoriesFilter.includes(element.SubCategory));
    }
    if (productsFilter.length > 0) {
      finalData = finalData.filter((element) => {
        const hasValidProducts =
          element.Product &&
          element.Product.length > 0 &&
          !element.Product.includes('NONE') &&
          element.Product != null;
        const matchesSelectedProducts =
          hasValidProducts && productsFilter.some((product) => element.Product.includes(product));
        return matchesSelectedProducts;
      });
    }
    if (statesFilter.length > 0) {
      finalData = finalData.filter((element) => {
        if (element.State.length == 0) {
          return element;
        } else if (statesFilter.some((state) => element.State.includes(state))) {
          return MediaElementAudioSourceNode;
        }
      });
    }
    if (commonlyUsedFilter === true) {
      finalData = finalData.filter(
        (element) => element.CommonlyUsed !== null && element.CommonlyUsed === 'Yes'
      );
    }

    if (orderableFilter === 'Yes') {
      finalData = finalData.filter(
        (element) => element.Orderable !== null && element.Orderable === 'Yes'
      );
    }
    if (orderableFilter === 'No') {
      finalData = finalData.filter(
        (element) => element.Orderable !== null && element.Orderable === 'No'
      );
    }

    if (divisionsFilter.length > 0) {
      finalData = finalData.filter(
        (element) => divisionsFilter.includes(element.Division) || element.Division === 'All Sales'
      );
    }

    setFilteredFormsList(finalData);
    setFiltering(false);
  };
  const updateFiltersCallback = (data, type) => {
    switch (type) {
      case 'products':
        setProductsFilter(data);
        break;
      case 'states':
        setStatesFilter(data);
        break;
      case 'categories':
        setCategoriesFilter(data);
        break;
      case 'divisions':
        setDivisionsFilter(data);
        break;
      case 'orderable':
        setOrderableFilter(data);
        break;
      case 'switches':
        setCommonlyUsedFilter(data.commonlyUsed);
        setOrderableFilter(data.orderable);
        break;
      case 'reset':
        setProductsFilter([]);
        setStatesFilter([]);
        setDivisionsFilter([]);
        setCategoriesFilter([]);
        setCommonlyUsedFilter(false);
        setOrderableFilter([]);
        setFilteredFormsList(formsList);
        break;
      default:
        break;
    }
  };

  const getFormsList = useQuery({
    queryKey: ['getFormsList'],
    queryFn: async () => await GetFormsList()(),
    enabled: false,
  });

  const getLOB = () => {
    setLOB(user.LOB.map((product) => product.ProductCode));
  };

  const getProductNamesQuery = useQuery({
    queryKey: ['getProductNames', 0],
    queryFn: async () => await GetProductNames()(),
  });

  // useEffect(()=>{
  //    getProductNames();
  //  }, []);

  useEffect(() => {
    if (getFormsList.isSuccess && getFormsList.data) {
      let parsed = JSON.parse(getFormsList.data);
      setFormsList(
        parsed.filter((form) => {
          return form;
        })
      );
      setDivisions(
        [...new Set([].concat(...parsed.map((form) => form.Division)))].filter(
          (d) => d === 'CASG' || d === 'ISD'
        )
      );
      setStates([...new Set([].concat(...parsed.map((form) => form.State)))]);
      setProducts([...new Set([].concat(...parsed.map((form) => form.Product)))]);
      setCategories([
        ...new Set(
          [].concat(
            ...parsed.map((form) => {
              if (
                form.SubCategory != null &&
                form.SubCategory.toString().trim() != '' &&
                form.SubCategory != 'None'
              ) {
                return form.SubCategory;
              }
            })
          )
        ),
      ]);
      setFiltering(true);
      setLoading(false);
    }
  }, [getFormsList.isSuccess, getFormsList.data]);

  useEffect(() => {
    if (getProductNamesQuery.isSuccess && getProductNamesQuery.data) {
      setProductNames(JSON.parse(getProductNamesQuery.data));
      setLoadingProductNames(false);
    }
  }, [getProductNamesQuery.isSuccess, getProductNamesQuery.data]);

  useEffect(() => {
    if (claims && productNames) {
      getLOB();
    }
  }, [claims, productNames]);
  useEffect(() => {
    if (LOB) {
      getFormsList.refetch();
    }
  }, [LOB]);
  useEffect(() => {
    filterTable();
  }, [
    categoriesFilter,
    statesFilter,
    productsFilter,
    commonlyUsedFilter,
    orderableFilter,
    divisionsFilter,
  ]);
  useEffect(() => {
    filterTable();
  }, [formsList]);
  useEffect(() => {}, [filteredFormsList]);

  return (
    <>
      <EmailFormModal form={formToEmail} setForm={setFormToEmail} />
      <Paper className="form-list-wrapper" square>
        <div className="filter-search-area">
          <div className="row  d-flex align-items-center justify-content-between">
            <TextFieldMemo
              id="form-seach"
              label="Search"
              inputRef={searchRef}
              disabled={loading}
              onBlur={() => filterTable()}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  {
                    filterTable();
                  }
                }
              }}
            />
            <div
              className={filterActive ? 'filter-icon active' : 'filter-icon'}
              onClick={() => (loading ? '' : filterIconClick())}
            >
              <FilterListIcon />
            </div>
          </div>
          <div className={filterActive ? 'row filters active' : 'row filters'}>
            <FiltersMemo
              states={states}
              products={products}
              productNames={productNames}
              categories={categories}
              divisions={divisions}
              updateFilters={updateFiltersCallback}
              loading={loading}
              loadingProductNames={loadingProductNames}
              LOB={LOB}
            />
          </div>
        </div>
        <TableContainer>
          {loading || loadingProductNames || filtering || !LOB ? (
            <>
              <TableLoadingSkeleton />
            </>
          ) : (
            <>
              <Table stickyHeader className="table outer-sticky-header" aria-label="Forms Table">
                <TableHead>
                  <TableRow>
                    <TableCell>Form Number</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell colSpan="2">Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFormsList.length == 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>No results</TableCell>
                    </TableRow>
                  ) : (
                    filteredFormsList
                      .sort()
                      .map((form) => (
                        <FormRow
                          form={form}
                          productNames={productNames}
                          key={form.Name + form.FormNumber + '-row'}
                          LOB={LOB}
                          setFormToEmail={setFormToEmail}
                        />
                      ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </TableContainer>
      </Paper>
    </>
  );
}

function FormRow(props) {
  const [open, setOpen] = useState(false);
  const [getPath, setGetPath] = useState(false);
  //const [filePath, loadStatus] = useCacheSPFile(props.form.FormURL, props.form.Name, getPath);
  const linkRef = useRef();
  const addToCart = useStore((state) => state.ADD_ITEM);
  //useEffect(()=>{
  //    if(filePath && loadStatus){
  //        if(loadStatus == 'success'){
  //            linkRef.current.click();
  //        }
  //    }
  //}, [filePath, loadStatus])

  const [fileIsFetching, setFileIsFetching] = useState(false);
  const handleSecuredFile = async (path) => {
    try {
      setFileIsFetching(true);
      await fetchSecuredFile(path);
    } catch (error) {
      setFileIsFetching(false);
    } finally {
      setFileIsFetching(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {props.form.FormNumber ? (
            props.form.FormNumber
          ) : (
            <div className="no-items">No form number</div>
          )}
        </TableCell>
        <TableCell>
          {props.form.Title ? (
            props.form.Title
          ) : props.form.Name ? (
            props.form.Name
          ) : (
            <div className="no-items">No form title or Name</div>
          )}
        </TableCell>
        <TableCell>
          {props.form.SubCategory ? (
            props.form.SubCategory
          ) : (
            <div className="no-items">No form category</div>
          )}
        </TableCell>
        <TableCell>
          <div className="form-actions">
            <Tooltip title="Form Information" placement="top" arrow>
              <span className="form-icon-wrapper">
                <InfoIcon onClick={() => (open ? setOpen(false) : setOpen(true))} />
              </span>
            </Tooltip>
            <Tooltip title="Open Preview" placement="top" arrow>
              <span
                className="form-icon-wrapper"
                onClick={() => {
                  //handleSecuredFile(`Forms\\${props.form.Name}`)
                  fetchSignedURL(
                    `${process.env.REACT_APP_AGENTPORTAL_PREFIX}/Forms/${props.form.Name}`
                  );
                  //window.open(`/Files/Forms/${props.form.Name}`, '_blank');
                }}
                style={{
                  cursor: 'pointer',
                }}
              >
                {fileIsFetching ? <CircularProgress size={16} /> : <OpenInNewIcon />}
              </span>
            </Tooltip>
            <Tooltip title="Email Form" placement="top" arrow>
              <span className="form-icon-wrapper">
                <EmailIcon
                  onClick={() =>
                    props.setFormToEmail({
                      FormFileName: props.form.Name,
                      FormName: props.form.Title,
                    })
                  }
                />
              </span>
            </Tooltip>
            {props.form.Orderable === 'Yes' && (props.form.Title || props.form.Name) ? (
              <Tooltip title="Add to Cart" placement="top" arrow>
                <span
                  className="form-icon-wrapper"
                  onClick={() => {
                    addToCart({
                      FormNumber: props.form.FormNumber,
                      FormName: props.form.Title ? props.form.Title : props.form.Name,
                      Quantity: '1',
                    });
                  }}
                >
                  <AddShoppingCartIcon />
                </span>
              </Tooltip>
            ) : (
              ''
            )}
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} className="collapsible-cell">
          <Collapse in={open}>
            <div className="form-details">
              <div className="form-details-item">
                <TableContainer>
                  <Table className="form-details-table inner-sticky-header" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Products</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {props.form.Product.length > 0 ? (
                        props.form.Product.map((product) => {
                          let productName = props.productNames.filter(
                            (productName) => productName.BaseProductId == product
                          );

                          if (productName.length > 0) {
                            //don't show advantage whole life or lifetime navigator
                            let label = productName[0].Title ? productName[0].Title : product;
                            return (
                              <TableRow key={product}>
                                <TableCell>{label}</TableCell>
                              </TableRow>
                            );
                          }

                          return false;
                        })
                      ) : (
                        <TableRow>
                          <TableCell className="no-items">No Products Specified</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
              <div className="form-details-item">
                <TableContainer>
                  <Table className="form-details-table inner-sticky-header" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>States</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {props.form.State.length > 0 ? (
                        props.form.State.map((state) => (
                          <TableRow key={state}>
                            <TableCell>{states[state]}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell className="no-items">No States Specified</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function Filters(props) {
  const user = useUser();
  let urlParams;
  let search = location.search.substring(1);
  if (search.length > 0) {
    urlParams = JSON.parse(
      '{"' + search.replace(/&/g, '","').replace(/=/g, '":"') + '"}',
      function (key, value) {
        return key === '' ? value : decodeURIComponent(value);
      }
    );
  }

  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orderable, setOrderable] = useState(['All']);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [productSnack, setProductSnack] = useState(false);
  const orderableValues = ['All', 'Yes', 'No'];
  const [switches, setSwitches] = useState({
    orderable: false,
    commonlyUsed: false,
    favorites: false,
  });

  const isValidProduct = () => {
    if (urlParams) {
      return props.products.includes(urlParams.product);
    }
  };
  const isValidCategory = () => {
    if (urlParams) {
      return props.categories.includes(urlParams.category);
    }
  };
  const handleUpdateOrderable = (e) => {
    setOrderable([e.target.value]);
    props.updateFilters(e.target.value, 'orderable');
  };
  const handleDivisionsFilterChange = (event) => {
    setSelectedDivisions(event.target.value);
  };
  const handleStateFilterChange = (event) => {
    setSelectedStates(event.target.value);
  };
  const handleProductFilterChange = (event) => {
    setSelectedProducts(event.target.value);
  };
  const handleCategoryFilterChange = (event) => {
    setSelectedCategories(event.target.value);
  };
  const handleSwitch = (event) => {
    setSwitches({ ...switches, [event.target.name]: event.target.checked });
  };
  const handleCommonlyUsed = (event) => {
    props.updateFilters(event.target.value, 'commonlyUsed');
  };

  function checkLOB(iD3Code) {
    if (props.LOB == undefined) return true;

    var isValid = props.LOB.find((o) => o == iD3Code);
    return isValid != null && isValid != undefined;
  }

  useEffect(() => {}, [
    props.states,
    props.products,
    props.categories,
    props.loading,
    props.loadingProductNames,
  ]);

  useEffect(() => {
    props.updateFilters(selectedDivisions, 'divisions');
  }, [selectedDivisions]);

  useEffect(() => {
    props.updateFilters(selectedStates, 'states');
  }, [selectedStates]);

  useEffect(() => {
    props.updateFilters(selectedProducts, 'products');
  }, [selectedProducts]);

  useEffect(() => {
    props.updateFilters(selectedCategories, 'categories');
  }, [selectedCategories]);

  useEffect(() => {
    props.updateFilters(switches, 'switches');
  }, [switches]);

  useEffect(() => {
    if (isValidProduct()) {
      setProductSnack(true);
      setSelectedProducts([urlParams.product]);
    }
  }, [props.products]);

  useEffect(() => {
    if (isValidCategory()) {
      setSelectedCategories([urlParams.category]);
    }
  }, [props.categories]);

  return (
    <>
      {urlParams && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={5000}
          open={productSnack}
          onClose={() => setProductSnack(false)}
        >
          <div id="product-snack-content">
            <InfoIcon /> Product filter set to {urlParams.product}
          </div>
        </Snackbar>
      )}
      <div className="row">
        <FormControlLabel
          control={
            <SwitchMemo
              checked={switches.commonlyUsed}
              onChange={handleSwitch}
              name="commonlyUsed"
              color="primary"
            />
          }
          label="Commonly Used"
          disabled={props.loading || props.loadingProductNames}
        />
        <FormControl
          className="col-12 col-sm-3"
          disabled={props.loading || props.loadingProductNames}
        >
          <InputLabel id="category-select-label">Orderable</InputLabel>
          <SelectMemo
            labelId="category-select-label"
            id="category-select-checkbox"
            value={orderable}
            onChange={handleUpdateOrderable}
            input={<Input />}
            renderValue={(selected) => selected.join(', ')}
          >
            {orderableValues.map((category, i) => (
              <MenuItemMemo key={category + '-' + i} value={category}>
                <Checkbox checked={orderable.indexOf(category) > -1} />
                <ListItemText primary={category} />
              </MenuItemMemo>
            ))}
          </SelectMemo>
        </FormControl>
        {user.isHO === true && (
          <FormControl
            className="col-12 col-sm-3"
            disabled={props.loading || props.loadingProductNames}
          >
            <InputLabel id="divisions-select-label">Division</InputLabel>
            <SelectMemo
              labelId="divisions-select-label"
              id="divisions-select-checkbox"
              multiple={true}
              value={selectedDivisions}
              onChange={handleDivisionsFilterChange}
              input={<Input />}
              renderValue={(selected) => selected.join(', ')}
              style={{ margin: '1em 0 1em 0' }}
            >
              {props.divisions.sort().map((division) => (
                <MenuItemMemo key={division} value={division}>
                  <Checkbox checked={selectedDivisions.indexOf(division) > -1} />
                  <ListItemText primary={divisions[division]} />
                </MenuItemMemo>
              ))}
            </SelectMemo>
          </FormControl>
        )}
        {/*
                <FormControlLabel
                    control={<SwitchMemo
                                checked={switches.favorites}
                                onChange={handleSwitch}
                                name="favorites" 
                                color="secondary"/>}
                    label = "Favorites"
                    />*/}{' '}
        {/*Enhancement: add favorites, tracked through cookies */}
      </div>
      <div className="row">
        <FormControl
          className="col-12 col-sm-3"
          disabled={props.loading || props.loadingProductNames}
        >
          <InputLabel id="category-select-label">Category</InputLabel>
          <SelectMemo
            labelId="category-select-label"
            id="category-select-checkbox"
            multiple={true}
            value={selectedCategories}
            onChange={handleCategoryFilterChange}
            input={<Input />}
            renderValue={(selected) => selected.join(', ')}
          >
            {props.categories.sort().map((category, i) => (
              <MenuItemMemo key={category + '-' + i} value={category}>
                <Checkbox checked={selectedCategories.indexOf(category) > -1} />
                <ListItemText primary={category} />
              </MenuItemMemo>
            ))}
          </SelectMemo>
        </FormControl>

        <FormControl
          className="col-12 col-sm-3"
          disabled={props.loading || props.loadingProductNames}
        >
          <InputLabel id="product-select-label">Product</InputLabel>
          <SelectMemo
            labelId="product-select-label"
            id="product-select-checkbox"
            multiple={true}
            value={selectedProducts}
            onChange={handleProductFilterChange}
            input={<Input />}
            renderValue={(selected) => selected.join(', ')}
          >
            {props.products.length > 0 ? (
              props.products.sort().map((product, i) => {
                let productName = props.productNames.filter(
                  (productName) => productName.BaseProductId == product
                );

                if (productName.length > 0 && checkLOB(productName[0].ProductId)) {
                  //don't show advantage whole life or lifetime navigator
                  let label = productName[0].Title ? productName[0].Title : product;
                  return (
                    <MenuItemMemo key={product + '-' + i} value={product}>
                      <Checkbox
                        key={product + '-' + i + 'productCheckbox'}
                        checked={selectedProducts.indexOf(product) > -1}
                      />
                      <ListItemText primary={label} />
                    </MenuItemMemo>
                  );
                }

                return false;
              })
            ) : (
              <MenuItemMemo key={'none' + '-' + 0} value={'none'}>
                <ListItemText primary={'No Products Specified'} />
              </MenuItemMemo>
            )}
          </SelectMemo>
        </FormControl>
        <FormControl
          className="col-12 col-sm-3"
          disabled={props.loading || props.loadingProductNames}
        >
          <InputLabel id="state-select-label">State</InputLabel>
          <SelectMemo
            labelId="state-select-label"
            id="state-select-checkbox"
            multiple={true}
            value={selectedStates}
            onChange={handleStateFilterChange}
            input={<Input />}
            renderValue={(selected) => selected.join(', ')}
          >
            {props.states.sort().map((state) => (
              <MenuItemMemo key={state} value={state}>
                <Checkbox checked={selectedStates.indexOf(state) > -1} />
                <ListItemText primary={states[state]} />
              </MenuItemMemo>
            ))}
          </SelectMemo>
        </FormControl>
      </div>
    </>
  );
}

function TableLoadingSkeleton() {
  let rows = [];
  if (rows.length == 0) {
    for (let i = 0; i <= 24; i++) {
      rows.push(
        <TableRow key={i}>
          <TableCell colSpan={4}>
            <Skeleton variant="rectangular" height={'3em'} width={'100%'} />
          </TableCell>
        </TableRow>
      );
    }
  }

  return (
    <>
      <Table stickyHeader className="table" aria-label="Forms Table">
        <TableHead>
          <TableRow>
            <TableCell>Form Name</TableCell>
            <TableCell colSpan="2">Form Number</TableCell>
            <TableCell colSpan="2">Category</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </>
  );
}

export { FormsList };
